"use client";

import { InputField, PasswordField } from "@/components/Fields";
import { getSession, signIn, useSession } from "next-auth/react";
import { useEffect, useState } from "react";

import BenefitsSidebar from "@/components/BenefitsSidebar";
import Image from "next/image";
import Link from "next/link";
import logo from "@/images/Orange Black.png";
import { useRouter } from "next/navigation";
import { useSearchParams } from "next/navigation";
import { FcGoogle } from "react-icons/fc";

/**
 * Decodes a base64-encoded JSON string with cross-environment support.
 * Handles both standard and URL-safe base64 encoding.
 *
 * @param {string | null | undefined} value
 * @returns {any | null}
 */
function decodeBase64Json(value) {
  if (!value) return null;
  const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");

  // Prefer browser decode; fall back to Buffer if a polyfill exists.
  if (typeof window !== "undefined" && typeof window.atob === "function") {
    const binary = window.atob(padded);
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    const text = new TextDecoder().decode(bytes);
    return JSON.parse(text);
  }

  if (typeof Buffer !== "undefined") {
    return JSON.parse(Buffer.from(padded, "base64").toString("utf8"));
  }

  throw new Error("Base64 decoder not available");
}

export default function SignIn() {
  const { data: session, status } = useSession();
  const searchParams = useSearchParams();
  const callbackUrl = searchParams.get("callbackUrl");
  const pipedriveState = searchParams.get("pipedrive_state");
  const pipedriveConnect = searchParams.get("pipedrive_connect");
  const pipedrivePending = searchParams.get("pipedrive_pending");
  const message = searchParams.get("message");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const router = useRouter();

  useEffect(() => {
    console.log("useEffect status", status);
    if (status === "loading") return;

    if (status === "authenticated" && session?.user?.id) {
      const token = {
        id: session.user.id,
        email: session.user.email,
      };

      localStorage.setItem("auth_token", JSON.stringify(token));

      // Handle different Pipedrive connection scenarios
      if (pipedriveState) {
        handlePipedriveConnection(session.user.id);
      } else if (pipedrivePending) {
        // Resume Pipedrive installation after sign-in
        handlePipedrivePendingInstall(session.user.id);
      } else {
        router.replace(callbackUrl || "/dashboard");
      }
    }
  }, [status]);

  // Handle Pipedrive connection
  const handlePipedriveConnection = async (userId) => {
    try {
      const state = decodeBase64Json(pipedriveState);

      // Complete the Pipedrive connection
      const response = await fetch('/api/pipedrive-extension/auth/connect', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pipedriveUserId: state.pipedriveUserId,
          pipedriveCompanyId: state.pipedriveCompanyId,
          userId,
        }),
      });

      if (response.ok && state.returnToExtension) {
        // Show success message and close window
        document.body.innerHTML = `
          <div style="display: flex; align-items: center; justify-content: center; min-height: 100vh; font-family: system-ui;">
            <div style="text-align: center; padding: 40px;">
              <div style="width: 64px; height: 64px; margin: 0 auto 24px; background: #10b981; border-radius: 50%; display: flex; align-items: center; justify-content: center;">
                <svg style="width: 32px; height: 32px; fill: white;" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                </svg>
              </div>
              <h2 style="font-size: 24px; margin-bottom: 16px;">Connected Successfully!</h2>
              <p style="color: #6b7280; margin-bottom: 24px;">You can now close this window and return to Pipedrive.</p>
              <button onclick="window.close()" style="background: #667eea; color: white; border: none; padding: 12px 24px; border-radius: 8px; cursor: pointer;">Close Window</button>
            </div>
          </div>
        `;
        setTimeout(() => window.close(), 3000);
      } else {
        router.replace('/dashboard');
      }
    } catch (error) {
      console.error('Pipedrive connection error:', error);
      setError('Failed to connect Pipedrive. Please try again.');
    }
  };

  // Handle pending Pipedrive installation after sign-in
  const handlePipedrivePendingInstall = async (userId) => {
    try {
      const pendingData = decodeBase64Json(pipedrivePending);

      // Complete the OAuth flow with the stored code
      window.location.href = `/api/pipedrive-extension/auth/complete?code=${pendingData.pipedriveCode}&userId=${userId}`;
    } catch (error) {
      console.error('Failed to resume Pipedrive installation:', error);
      router.replace('/settings?error=pipedrive_install_failed');
    }
  };

  // Animation effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const handleGoogleSignIn = async () => {
    await signIn("google", {
      callbackUrl: "/dashboard",
    });
  };

  const handleSalesablySignIn = async () => {
    await signIn("salesably", {
      callbackUrl: callbackUrl || "/dashboard",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    try {
      setLoading(true);

      // Call signIn with 'credentials' and the email, password
      const result = await signIn("credentials", {
        redirect: false,
        email,
        password,
        callbackUrl: callbackUrl || "/dashboard",
      });

      if (result.error) {
        // Handle errors like invalid credentials
        setError(result.error);
      } else {
        console.log("Redirecting...");


        const updatedSession = await getSession();
        console.log("Updated session after sign in:", updatedSession);
  
        if (updatedSession?.user) {
          localStorage.setItem(
            "auth_token",
            JSON.stringify({
              id: updatedSession.user.id,
              email: updatedSession.user.email,
            })
          );
          console.log("Storing local token", {
            id: updatedSession.user.id,
            email: updatedSession.user.email,
          });
        }

        router.push(callbackUrl || "/dashboard");
      }
    } catch (error) {
      console.error("An unexpected error occurred:", error);
      setError("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    "Research companies instantly - always FREE",
    "Get 5 free credits for people research",
    "Pipedrive CRM bidirectional sync included",
  ];

  return (
    <div className="flex flex-col min-h-screen lg:flex-row">
      <div className="hidden lg:block lg:w-2/5">
        <BenefitsSidebar
          title="Join Every Call Prepared"
          benefits={benefits}
        />
      </div>
      <div className="relative flex items-center justify-center flex-1 p-6 sm:p-10 lg:w-3/5 min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-950">
        {/* Subtle background pattern */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(249,115,22,0.05),transparent_50%)]" />

        <div className="relative w-full max-w-md">
          <div className={`px-8 py-10 bg-white dark:bg-slate-800 rounded-2xl shadow-xl shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-200/50 dark:border-slate-700/50 transition-all duration-700 ease-out ${
            isVisible
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 translate-y-8'
          }`}>
            {/* Header */}
            <div className="text-center mb-8">
              <Image src={logo} alt="Prep5 Logo" width={140} height={46} className="mx-auto" />
              <h2 className="mt-6 text-2xl font-bold text-gray-900 dark:text-white">
                Welcome back
              </h2>
              <p className="mt-2 text-sm text-gray-600 dark:text-slate-400">
                Sign in to <span className="font-medium text-gray-700 dark:text-slate-300">Prep5</span> by Salesably
              </p>
            </div>

            {/* Alerts */}
            {message && (
              <div className="p-4 mb-6 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800" role="alert">
                <p className="text-sm text-blue-700 dark:text-blue-300">{message}</p>
              </div>
            )}
            {pipedrivePending && (
              <div className="p-4 mb-6 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800" role="alert">
                <p className="font-semibold text-green-700 dark:text-green-300">Pipedrive Connection Pending</p>
                <p className="text-sm mt-1 text-green-600 dark:text-green-400">Please sign in to complete your Pipedrive extension setup.</p>
              </div>
            )}
            {error && (
              <div className="p-4 mb-6 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800" role="alert">
                <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              <InputField
                id="email"
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <PasswordField
                id="password"
                label="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                showPassword={showPassword}
                toggleShowPassword={() => setShowPassword(!showPassword)}
                required
              />
              <div className="flex items-center justify-between">
                <label className="inline-flex items-center cursor-pointer">
                  <input type="checkbox" className="w-4 h-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500 dark:border-slate-600 dark:bg-slate-700" />
                  <span className="ml-2 text-sm text-gray-600 dark:text-slate-400">Remember me</span>
                </label>
                <Link
                  href="/forgot-password"
                  className="text-sm font-medium text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300"
                >
                  Forgot password?
                </Link>
              </div>
              <button
                type="submit"
                disabled={loading}
                className={`flex w-full justify-center items-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold text-white shadow-lg transition-all duration-300 ${
                  loading
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700 shadow-blue-500/25 hover:shadow-blue-500/40"
                } focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600`}
              >
                {loading ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Signing in...
                  </>
                ) : "Sign in"}
              </button>
            </form>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200 dark:border-slate-700"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white dark:bg-slate-800 text-gray-500 dark:text-slate-400">or continue with</span>
              </div>
            </div>

            {/* SSO Buttons */}
            <div className="space-y-3">
              <button
                onClick={handleGoogleSignIn}
                className="flex items-center justify-center w-full gap-3 px-4 py-3 text-sm font-semibold text-gray-700 dark:text-slate-200 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-xl shadow-sm hover:bg-gray-50 dark:hover:bg-slate-600 transition-colors"
              >
                <FcGoogle className="w-5 h-5" />
                Sign in with Google
              </button>
              <button
                type="button"
                onClick={handleSalesablySignIn}
                className="flex items-center justify-center w-full gap-3 px-4 py-3 text-sm font-semibold text-gray-700 dark:text-slate-200 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-xl shadow-sm hover:bg-gray-50 dark:hover:bg-slate-600 transition-colors"
              >
                <Image src="/favicon-32x32.png" alt="Salesably" width={20} height={20} />
                Sign in with Salesably
              </button>
            </div>

            {/* Footer */}
            <p className="mt-6 text-xs text-center text-gray-500 dark:text-slate-400">
              By signing in, you agree to our{" "}
              <Link href="/terms-of-service" className="text-orange-600 hover:underline dark:text-orange-400">
                Terms
              </Link>{" "}
              and{" "}
              <Link href="/privacy-policy" className="text-orange-600 hover:underline dark:text-orange-400">
                Privacy Policy
              </Link>
            </p>

            <p className="mt-6 text-center text-sm text-gray-600 dark:text-slate-400">
              Don&apos;t have an account?{" "}
              <Link
                href="/sign-up"
                className="font-semibold text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300"
              >
                Sign up free
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
